import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Row, Col, Button, Clearfix, Label, DropdownButton, MenuItem } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import { size as _size, indexOf as _indexOf } from 'lodash';
import { ArcherContainer, ArcherElement } from 'react-archer';

import { addNotification } from 'actions/NotificationsActions';
import { whiteLabelSettingsSingleOptionFormatter } from 'components/forms/autocomplete/WhiteLabelSettings';
import { getCalculatedHoldingCompanyIndex, getCalculatedHoldingCompanyInfos } from 'helpers/clients';

import EntityPropsUpdater from 'component/EntityPropsUpdater';

import RoundImage from 'components/layout/RoundImage';
import FormAutocomplete from 'components/forms/FormAutocomplete';

import Data from 'components/Data';

import ClientParentChange from 'components/modals/ClientParentChange';
import ClientGrantedHoldingAccessChange from 'components/modals/ClientGrantedHoldingAccessChange';

import { ROLE_COMPANY, ROLE_ROOT_EMPLOYEE } from 'js/constants';
import may from 'js/security';

class ClientHolding extends EntityPropsUpdater {
  constructor(props) {
    super(props);

    this.state = {
      showParentChange: false,
      newParentUsername: null,
      showGrantedHoldingAccessChange: false,
      showSiblings: false,
      showDescendants: false,
    };
  }

  parentChangeClick = (e, newParentUsername = null) => {
    this.setState({ showParentChange: true, newParentUsername });
  };

  closeParentChange = () => {
    this.setState({ showParentChange: false, newParentUsername: null });
  };

  showSiblings = () => {
    this.setState({ showSiblings: true });
  };

  closeSiblings = () => {
    this.setState({ showSiblings: false });
  };

  showDescendants = () => {
    this.setState({ showDescendants: true });
  };

  closeDescendants = () => {
    this.setState({ showDescendants: false });
  };

  showGrantedHoldingAccessChange = (
    e,
    grantedHoldingAccessAction,
    newGrantedHoldingAccess,
    newGrantedHoldingAccessDepth,
    gratendHoldingAccessUsername
  ) => {
    this.setState({
      showGrantedHoldingAccessChange: true,
      grantedHoldingAccessAction,
      newGrantedHoldingAccess,
      newGrantedHoldingAccessDepth,
      gratendHoldingAccessUsername,
    });
  };

  closeGrantedHoldingAccessChange = () => {
    this.setState({ showGrantedHoldingAccessChange: false });
  };

  componentDidUpdate = prevProps => {
    const {
      intl: { messages },
      holdingInfosFormStatus,
      doNotify,
    } = this.props;

    // new parent was saved to the client
    if (
      prevProps.holdingInfosFormStatus.pending &&
      !holdingInfosFormStatus.pending &&
      !holdingInfosFormStatus.hasError
    ) {
      this.closeParentChange();
      this.closeGrantedHoldingAccessChange();
      doNotify(1, messages.holding_info_saved);
    }
  };

  onCompanyAction = action => {
    const {
      holdingInfos: {
        data: holdingInfos,
        data: {
          current_granted_holding_access: grantedHoldingAccess,
          current_granted_holding_access_depth: grantedHoldingAccessDepth,
          whitelist,
          blacklist,
        },
      },
    } = this.props;
    const [type, id] = action.split(':');
    const { companyIndex, parentIndex } = getCalculatedHoldingCompanyIndex(holdingInfos, id);

    const isWhitelisted = _indexOf(whitelist, id) >= 0;
    const isBlacklisted = _indexOf(blacklist, id) >= 0;

    switch (type) {
      case 'setNewParent': {
        this.parentChangeClick(null, id);
        break;
      }

      case 'grantHoldingAccess': {
        // calculate how the access will change
        let newGrantedHoldingAccess = grantedHoldingAccess;
        let newGrantedHoldingAccessDepth = grantedHoldingAccessDepth;
        if (!isBlacklisted) {
          if (companyIndex > parentIndex) {
            // the clicked company is below the parent
            // we need to manipulate the newGrantedHoldingAccessDepth value
            newGrantedHoldingAccessDepth = companyIndex - parentIndex;
          } else {
            // the clicked company is above the parent
            // we need to manipulate the newGrantedHoldingAccess value
            newGrantedHoldingAccess = companyIndex - parentIndex;
          }
        }

        this.showGrantedHoldingAccessChange(null, 'grant', newGrantedHoldingAccess, newGrantedHoldingAccessDepth, id);
        break;
      }

      case 'denyHoldingAccess': {
        // calculate how the access will change
        let newGrantedHoldingAccess = grantedHoldingAccess;
        let newGrantedHoldingAccessDepth = grantedHoldingAccessDepth;
        if (!isWhitelisted) {
          if (companyIndex > parentIndex) {
            // the clicked company is below the parent
            // we need to manipulate the newGrantedHoldingAccessDepth value
            newGrantedHoldingAccessDepth = companyIndex - parentIndex - 1;
          } else {
            // the clicked company is above the parent
            // we need to manipulate the newGrantedHoldingAccess value
            newGrantedHoldingAccess = companyIndex - parentIndex + 1;
          }
        }

        this.showGrantedHoldingAccessChange(null, 'deny', newGrantedHoldingAccess, newGrantedHoldingAccessDepth, id);
        break;
      }

      case 'showSiblings': {
        this.showSiblings();
        break;
      }

      case 'closeSiblings': {
        this.closeSiblings();
        break;
      }

      case 'showDescendants': {
        this.showDescendants();
        break;
      }

      case 'closeDescendants': {
        this.closeDescendants();
        break;
      }
    }
  };

  renderSiblings = () => {
    const {
      holdingInfos: {
        data: { siblings },
      },
    } = this.props;

    const res = [];
    for (let i = 0; i < siblings.length; i++) {
      const relations = [];

      res.push(
        <ArcherElement key={siblings[i].id} id={siblings[i].id} relations={relations}>
          <div className="col-xs-10 col-md-4 bg-rad-lg bg-master-lightest shadow-1 m-b-30">
            {this.renderCompany(siblings[i])}
          </div>
        </ArcherElement>
      );
    }

    return res;
  };

  renderDescendants = () => {
    const {
      holdingInfos: {
        data: { descendants },
      },
    } = this.props;

    const res = [];
    for (let i = 0; i < descendants.length; i++) {
      const relations = [];

      res.push(
        <ArcherElement key={descendants[i].id} id={descendants[i].id} relations={relations}>
          <div className="col-xs-10 col-md-4 bg-rad-lg bg-master-lightest shadow-1 m-b-30">
            {this.renderCompany(descendants[i])}
          </div>
        </ArcherElement>
      );
    }

    return res;
  };

  renderCompany = company => {
    const {
      // intl: { messages },
      holdingInfos: {
        data: holdingInfos,
        data: { descendants },
      },
    } = this.props;
    const { showSiblings, showDescendants } = this.state;

    const {
      isCompany,
      companyIndex,
      isSibling,
      isDescendant,
      isWhitelisted,
      isBlacklisted,
      isDirectParent,
      isCurrentUser,
      mayReadData,
      mayGrantAccess,
    } = getCalculatedHoldingCompanyInfos(holdingInfos, company);

    const actions = [];
    if (companyIndex && !isCurrentUser && mayGrantAccess) {
      if (!isDirectParent) {
        actions.push(
          <MenuItem key="setNewParent" eventKey={`setNewParent:${company.id}`}>
            set as parent
          </MenuItem>
        );

        if (mayReadData) {
          actions.push(
            <MenuItem key="unsetHoldingAccess" eventKey={`denyHoldingAccess:${company.id}`}>
              deny data access
            </MenuItem>
          );
        }
      }

      if (!mayReadData) {
        actions.push(
          <MenuItem key="setHoldingAccess" eventKey={`grantHoldingAccess:${company.id}`}>
            grant data access
          </MenuItem>
        );
      }
    }

    if ((isDirectParent && !isCompany) || isCurrentUser) {
      if (actions.length) {
        actions.push(<MenuItem divider key="siblingsDivider" />);
      }

      if (showSiblings) {
        actions.push(
          <MenuItem eventKey="closeSiblings" key="closeSiblings">
            hide siblings
          </MenuItem>
        );
      } else if (!showDescendants) {
        actions.push(
          <MenuItem eventKey="showSiblings" key="showSiblings">
            show siblings
          </MenuItem>
        );

        if (_size(descendants)) {
          actions.push(
            <MenuItem eventKey="showDescendants" key="showDescendants">
              show descendants
            </MenuItem>
          );
        }
      } else {
        actions.push(
          <MenuItem eventKey="closeDescendants" key="closeDescendants">
            hide descendants
          </MenuItem>
        );
      }
    }

    return (
      <>
        <div className="p-t-15">
          {(isDirectParent || mayReadData || isCurrentUser || isSibling || isDescendant) && (
            <div className="m-b-10">
              {isCurrentUser && <Label bsStyle="info">Current</Label>}{' '}
              {isDirectParent && <Label bsStyle="primary">Direct parent</Label>}{' '}
              {(mayReadData || isCurrentUser) && <Label bsStyle="warning">Has data access</Label>}{' '}
              {isSibling && <Label bsStyle="info">Sibling</Label>}{' '}
              {isDescendant && <Label bsStyle="info">Descendant</Label>}{' '}
              {isWhitelisted && <Label bsStyle="info">Whitelisted</Label>}{' '}
              {isBlacklisted && <Label bsStyle="danger">Blacklisted</Label>}{' '}
            </div>
          )}

          <div className="relative">
            <div className="top-left">
              {company.avatar ? (
                <RoundImage size={33} url={company.avatar} usePixels />
              ) : (
                <span className="fa-stack fa-lg" style={{ fontSize: '16.5px' }}>
                  <i className="fa fa-circle fa-stack-2x hint-text" />
                  <i className="fa fa-address-book fa-stack-1x fa-inverse" />
                </span>
              )}
            </div>

            <div className={'m-l-40 ' + (actions.length ? 'm-r-30' : '')}>
              <div className="bold overflow-ellipsis">{company.company}</div>
              <div className="small hint-text">ID: {company.id}</div>
            </div>

            {!!actions.length && (
              <div className="top-right">
                <DropdownButton
                  bsSize="sm"
                  noCaret
                  title={<i className="fas fa-ellipsis-v" />}
                  onSelect={this.onCompanyAction}
                  className="no-border bg-transparent"
                >
                  {actions}
                </DropdownButton>
              </div>
            )}
          </div>
        </div>
        <div className="p-t-10 p-b-15">
          {company.street} {company.house_number} {company.apartment_number}
          <br />
          {company.zip_code} {company.city}
        </div>
      </>
    );
  };

  render() {
    const {
      intl: { messages },
      entityLoaded,
      entity,
      inModal,
      holdingInfos,
      whiteLabelProject,
      user,
    } = this.props;
    const {
      showParentChange,
      newParentUsername,
      showGrantedHoldingAccessChange,
      grantedHoldingAccessAction,
      newGrantedHoldingAccess,
      newGrantedHoldingAccessDepth,
      gratendHoldingAccessUsername,
      showSiblings,
      showDescendants,
    } = this.state;

    const isCompany = entityLoaded && may(ROLE_COMPANY, entity.getRoles());
    const isRootEmployee = may(ROLE_ROOT_EMPLOYEE, user.roles);
    const anchorOptions = {
      sourceAnchor: 'bottom',
      targetAnchor: 'top',
      style: { strokeColor: 'blue', strokeWidth: 1 },
    };

    return (
      <div className="clearfix padding-20 sm-padding-10">
        {entityLoaded && (
          <div>
            <Row>
              <Data dataName="holdingInfos" url={`/api/clients/${entity.getUsername()}/holdinginfos.json`} />
              {!!entity.getDefaultWhiteLabelProject() && (
                <Data dataName="whiteLabel" url={`/api/whitelabels/${entity.getDefaultWhiteLabelProject()}.json`} />
              )}

              <Col xs={12} md={inModal ? 12 : isRootEmployee ? 8 : 12}>
                <div className="panel panel-transparent">
                  <div className="panel-body">
                    <p>
                      {messages.holding_structure}
                      {holdingInfos.pending && <i className="m-l-10 fa fa-circle-notch fa-spin" />}
                    </p>

                    <ArcherContainer>
                      {!holdingInfos.pending && !!holdingInfos.data.ancestors.length && (
                        <div className="clearfix">
                          {holdingInfos.data.ancestors.map((ancestor, i) => {
                            // list the relations to display the arrows
                            // start with the ancestors relations
                            const relations = [];
                            if (i + 1 < holdingInfos.data.ancestors.length) {
                              relations.push({
                                targetId: holdingInfos.data.ancestors[i + 1].id,
                                sourceAnchor: 'bottom',
                                targetAnchor: 'top',
                                style: { strokeColor: 'blue', strokeWidth: 1 },
                              });
                            }
                            if (isCompany && i + 1 === holdingInfos.data.ancestors.length) {
                              relations.push({
                                targetId: entity.getUsername(),
                                ...anchorOptions,
                              });
                            }

                            // if we should list the siblings, create arrows for the siblings too
                            if (
                              showSiblings &&
                              ((!isCompany && i + 2 === holdingInfos.data.ancestors.length) ||
                                (isCompany && i + 1 === holdingInfos.data.ancestors.length))
                            ) {
                              relations.push(
                                ...holdingInfos.data.siblings.map(sibling => ({
                                  targetId: sibling.id,
                                  ...anchorOptions,
                                }))
                              );
                            }

                            // if we should list the descendants, create arrows for the descendants too
                            if (showDescendants && (!isCompany && i + 1 === holdingInfos.data.ancestors.length)) {
                              relations.push(
                                ...holdingInfos.data.descendants.map(descendant => ({
                                  targetId: descendant.id,
                                  ...anchorOptions,
                                }))
                              );
                            }

                            return (
                              <React.Fragment key={ancestor.id}>
                                <ArcherElement id={ancestor.id} relations={relations}>
                                  <div className="col-xs-10 col-md-4 bg-rad-lg bg-master-lightest shadow-1 m-b-30">
                                    {this.renderCompany(ancestor)}
                                  </div>
                                </ArcherElement>

                                {showSiblings &&
                                  !isCompany &&
                                  i + 1 === holdingInfos.data.ancestors.length &&
                                  this.renderSiblings()}

                                <Clearfix />

                                {showDescendants && !isCompany && i + 1 === holdingInfos.data.ancestors.length && (
                                  <>
                                    {this.renderDescendants()}
                                    <Clearfix />
                                  </>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      )}

                      {!holdingInfos.pending && isCompany && (
                        <div className="clearfix">
                          <ArcherElement
                            id={entity.getUsername()}
                            relations={
                              showDescendants
                                ? holdingInfos.data.descendants.map(descendant => ({
                                    targetId: descendant.id,
                                    ...anchorOptions,
                                  }))
                                : []
                            }
                          >
                            <div className="col-xs-10 col-md-4 bg-rad-lg bg-master-lightest shadow-1 m-b-30">
                              {this.renderCompany({
                                id: entity.getUsername(),
                                company: entity.getProfile().getCompany(),
                                street: entity.getProfile().getStreet(),
                                house_number: entity.getProfile().getHouseNumber(),
                                apartment_number: entity.getProfile().getApartmentNumber(),
                                zip_code: entity.getProfile().getZipCode(),
                                city: entity.getProfile().getCity(),
                              })}
                            </div>
                          </ArcherElement>

                          {showSiblings && this.renderSiblings()}

                          <Clearfix />

                          {showDescendants && (
                            <>
                              {this.renderDescendants()}
                              <Clearfix />
                            </>
                          )}
                        </div>
                      )}
                    </ArcherContainer>

                    {/* show the possibility to view descendants when they exist */}
                    {!holdingInfos.pending &&
                      !!holdingInfos.data.ancestors.length &&
                      !!holdingInfos.data.descendants.length && <></>}

                    {/* we can't show anything, because there are no ancestors; inform the user */}
                    {!holdingInfos.pending && !holdingInfos.data.ancestors.length && (
                      <>{messages.client_has_no_ancestors}</>
                    )}
                  </div>
                </div>
              </Col>

              {isRootEmployee && (
                <Col xs={12} md={inModal ? 12 : 4}>
                  <div className="panel panel-transparent">
                    <div className="panel-body">
                      <p className="form-group-title">{messages.change_parent}</p>
                      <Button onClick={this.parentChangeClick}>{messages.change_parent}</Button>

                      {isCompany && (
                        <>
                          <p className="m-t-15 form-group-title">
                            Standart White Label Projekt{' '}
                            {!!entity.getDefaultWhiteLabelProject() && whiteLabelProject.pending && (
                              <i className="fa fa-circle-notch fa-spin" />
                            )}
                          </p>
                          <div className="form-group-attached">
                            {(!entity.getDefaultWhiteLabelProject() || !whiteLabelProject.pending) && (
                              <FormAutocomplete
                                name="defaultWhiteLabelProject"
                                autocompleteType="whiteLabelSettings"
                                value={
                                  entity.getDefaultWhiteLabelProject()
                                    ? whiteLabelSettingsSingleOptionFormatter(whiteLabelProject.data)
                                    : null
                                }
                                onChange={this.updateEntity}
                              />
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </div>
        )}

        <div id="parent-change">
          <ClientParentChange
            client={entity}
            show={showParentChange}
            parentUsername={newParentUsername}
            close={this.closeParentChange}
          />
        </div>
        <div id="client-holding-access-change">
          <ClientGrantedHoldingAccessChange
            client={entity}
            show={showGrantedHoldingAccessChange}
            close={this.closeGrantedHoldingAccessChange}
            grantedHoldingAccessAction={grantedHoldingAccessAction}
            newGrantedHoldingAccess={newGrantedHoldingAccess}
            newGrantedHoldingAccessDepth={newGrantedHoldingAccessDepth}
            gratendHoldingAccessUsername={gratendHoldingAccessUsername}
          />
        </div>
      </div>
    );
  }
}
ClientHolding.defaultProps = {
  inModal: false,
};

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
    holdingInfos: state.data.holdingInfos,
    holdingInfosFormStatus: state.formStatus.holdingInfos,
    whiteLabelProject: state.data.whiteLabel,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    doNotify: (code, text) => {
      dispatch(addNotification(code, text));
    },
  };
};

export default withRouter(
  injectIntl(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(ClientHolding)
  )
);
